import axios from '@providers/axiosProvider';
import * as actions from './actions';
import { formatDateString, checkComparisonIsAfter, getDateNow } from '@utils/dateUtils';

import {
  APIQualificationsResponse,
  APISetSpecialQualificationRequest,
  APISpecQualificationsResponse,
  SpecialQualification,
  SpecialQualificationSubmitData,
  APIQualificationHistoryResponse,
  Qualification,
  SpecialQualificationReference,
  APIQualificationDetailResponse,
  APIPropertyQualificationMapResponse,
  APIUseSpecialQualificationRequest,
  APIQualifications,
  APIQualificationDefinition,
  APICreateQualifications,
  QualificationDefinition,
  QualificationAssignStatus,
  AffilicationTypeDefinition,
  WorkTypeDefinition,
} from './types';

/**
 * @summary 法廷資格の届出の登録を行う
 * @param {number} propId 建物ID
 * @param {number} qualificationId 資格ID
 * @param {APICreateQualifications} data 届出情報
 * @returns {Promise<boolean>} 登録成否
 */
export const createQualificationAsync =
  (propId: number, qualificationId: number, data: APICreateQualifications) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .post<APIQualificationDetailResponse>(
        `/notification_qualifications/${propId}/${qualificationId}`,
        data,
      )
      .then((response) => {
        const propertyId = response.data.result.property_id;
        dispatch(actions.setIsNavigating(true));
        dispatch(actions.setIsNavigatingTo(propertyId));
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 法廷資格の届出の編集を行う
 * @param {number} propId 建物ID
 * @param {number} qualificationId 資格ID
 * @param {APICreateQualifications} data 届出情報
 * @returns {Promise<boolean>} 登録成否
 */
export const updateQualificationAsync =
  (propId: number, qualificationId: number, data: APICreateQualifications) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .put<APIQualificationDetailResponse>(
        `/notification_qualifications/${propId}/${qualificationId}`,
        data,
      )
      .then(() => {
        dispatch(actions.setIsNavigating(true));
        dispatch(actions.setIsNavigatingTo(propId));
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        dispatch(actions.setIsNavigating(true));
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * 法定資格管理 履歴削除
 * @param specId
 * @returns
 */
export const deleteQualificationHistoryAsync =
  (propertyId: number, propQualificationId: number) => async (dispatch: any) => {
    dispatch(actions.setIsSubmitting(true));

    await axios
      .delete(`/notification_qualifications/${propertyId}/history/${propQualificationId}`, {})
      .then(() => {
        dispatch(actions.refresh());
      })
      .catch(() => {});

    dispatch(actions.setIsSubmitting(false));
  };

/**
 * 法定資格管理 個別資格 削除
 * @param specId
 * @returns
 */
export const deleteSpecialQualificationAsync =
  (propQualificationId: number) => async (dispatch: any) => {
    dispatch(actions.setIsSubmitting(true));

    await axios
      .delete(`/notification_qualifications/individual/${propQualificationId}`, {})
      .then(() => {
        dispatch(actions.refresh());
      })
      .catch(() => {});

    dispatch(actions.setIsSubmitting(false));
  };

/**
 * @summary 法定資格管理 個別資格 追加
 * @param {SpecialQualificationSubmitData} data 個別資格情報
 * @returns {Promise<boolean>} 登録成否
 */
export const createSpecialQualificationAsync =
  (data: SpecialQualificationSubmitData) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    const requestData: APISetSpecialQualificationRequest = {
      individual_qualification_name: data.qualificationName,
      remarks: data.description,
    };

    return await axios
      .post(`/notification_qualifications/individual`, requestData)
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 法定資格管理 個別資格 編集
 * @param {number} specId 個別資格ID
 * @param {SpecialQualificationSubmitData} data 個別資格情報
 * @returns {Promise<boolean>} 登録成否
 */
export const updateSpecialQualificationAsync =
  (specId: number, data: SpecialQualificationSubmitData) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    const requestData: APISetSpecialQualificationRequest = {
      individual_qualification_name: data.qualificationName,
      remarks: data.description,
    };

    return await axios
      .put(`/notification_qualifications/individual/${specId}`, requestData)
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * 法定資格管理 個別資格振り分け 一覧
 * @param propertyId
 * @returns
 */
export const getSpecialQualificationListReferedInPropertyAsync =
  (propId: number) => async (dispatch: any) => {
    dispatch(actions.setIsLoading(true));

    await axios
      .get<APIPropertyQualificationMapResponse>(
        `/notification_qualifications/${propId}/reference`,
        {
          params: null,
        },
      )
      .then((response) => {
        const result = response.data.result?.list?.map(
          (l): SpecialQualificationReference => ({
            individual_qualification_id: l.qualification_id,
            individual_qualification_name: l.qualification_name,
            remarks: l.remarks,
            is_allocated: l.is_allocated,
          }),
        );

        dispatch(actions.setSpecQualificationReferences(result));
      })
      .catch(() => {
        dispatch(actions.setSpecQualificationReferences([]));
      });

    dispatch(actions.setIsLoading(false));
  };

/**
 * @summary 法定資格管理 個別資格設定
 * @param {number} propertyId 建物ID
 * @param {APIUseSpecialQualificationRequest} data 個別資格設定情報
 * @returns {Promise<boolean>} 登録成否
 */
export const updateSpecialQualificationListReferedInPropertyAsync =
  (propId: number, data: APIUseSpecialQualificationRequest) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .put<APIPropertyQualificationMapResponse>(
        `/notification_qualifications/${propId}/reference`,
        data,
      )
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        dispatch(actions.setSpecQualificationReferences([]));
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * 法定資格管理 履歴一覧
 * @param propertyId
 * @param qualificationId
 * @returns
 */
export const getStandardQualificationHistoryAsync =
  (propId: number, qualificationId: number) => async (dispatch: any) => {
    dispatch(actions.setIsHistoryLoading(true));

    await axios
      .get<APIQualificationHistoryResponse>(
        `/notification_qualifications/${propId}/history/national/${qualificationId}`,
        {
          params: null,
        },
      )
      .then((response) => {
        const result = response.data.result;

        const specs: Qualification[] = result.map((q): Qualification => toQualificationModel(q));

        dispatch(actions.setQualificationHistory(specs));
      })
      .catch(() => {
        dispatch(actions.setSpecQualifications([]));
      });

    dispatch(actions.setIsHistoryLoading(false));
  };

/**
 * 法定資格管理 履歴一覧
 * @param propertyId
 * @param qualificationId
 * @returns
 */
export const getSpecialqualificationHistoryAsync =
  (propertyId: number, qualificationId: number) => async (dispatch: any) => {
    dispatch(actions.setIsHistoryLoading(true));

    await axios
      .get<APIQualificationHistoryResponse>(
        `/notification_qualifications/${propertyId}/history/individual/${qualificationId}`,
        {
          params: null,
        },
      )
      .then((response) => {
        const result = response.data.result;

        const specs: Qualification[] = result.map((q): Qualification => toQualificationModel(q));

        dispatch(actions.setQualificationHistory(specs));
      })
      .catch(() => {
        dispatch(actions.setSpecQualifications([]));
      });

    dispatch(actions.setIsHistoryLoading(false));
  };

/**
 * 法定資格管理 個別資格 一覧
 * @returns
 */
export const getSpecialQualificationsAsync = () => async (dispatch: any) => {
  dispatch(actions.setIsLoading(true));

  await axios
    .get<APISpecQualificationsResponse>('/notification_qualifications/individual', {
      params: null,
    })
    .then((response) => {
      const result = response.data.result;

      const specs: SpecialQualification[] = result.list.map(
        (sq): SpecialQualification => ({
          id: sq.individual_qualification_id,
          qualificationName: sq.individual_qualification_name,
          description: sq.remarks,
          isDeleted: false,
        }),
      );
      dispatch(actions.setSpecQualifications(specs));
    })
    .catch(() => {
      dispatch(actions.setSpecQualifications([]));
    });

  dispatch(actions.setIsLoading(false));
};

export const getQualificationDetailAsync =
  (propId: number, specId: number) => async (dispatch: any) => {
    dispatch(actions.setIsDetailLoading(true));

    await axios
      .get<APIQualificationDetailResponse>(`/notification_qualifications/${propId}/${specId}`, {
        params: null,
      })
      .then((response) => {
        const result = toQualificationModel(response.data.result);
        dispatch(actions.setQualificationDetail(result));
      })
      .catch(() => {});

    dispatch(actions.setIsDetailLoading(false));
  };

/**
 * 法定資格管理 一覧
 * @returns
 */
export const getQualificationsAsync = (propId: number) => async (dispatch: any) => {
  dispatch(actions.setIsLoading(true));

  await axios
    .get<APIQualificationsResponse>(`/notification_qualifications/${propId}`, {
      params: null,
    })
    .then((response) => {
      const result = response.data.result;

      const specs: Qualification[] = result.list.map((q): Qualification => toQualificationModel(q));
      const definitions: QualificationDefinition[] = result.definitions.map(
        (d): QualificationDefinition => toQualificationDefinitionModel(d),
      );
      const affiliations: AffilicationTypeDefinition[] = result.affiliation_types;
      const workTypes: WorkTypeDefinition[] = result.work_types;

      dispatch(actions.setQualifications(specs));
      dispatch(actions.setQualificationDefinitions(definitions));
      dispatch(actions.setMstWorkTypes(workTypes));
      dispatch(actions.setAffiliationTypes(affiliations));
    })
    .catch(() => {
      dispatch(actions.setSpecQualifications([]));
    });

  dispatch(actions.setIsLoading(false));
};

const toQualificationDefinitionModel = (d: APIQualificationDefinition): QualificationDefinition => {
  const definition: QualificationDefinition = {
    id: d.id,
    title: d.name,
    isNational: d.is_national,
  };
  return definition;
};

const getAssignmentStatus = (
  appointment_date: string,
  dismissal_date: string,
): QualificationAssignStatus => {
  const today = formatDateString(getDateNow(), 'ui');

  if (checkComparisonIsAfter(today, dismissal_date)) {
    return 'PAST';
  }
  return appointment_date == ''
    ? ''
    : checkComparisonIsAfter(appointment_date, today)
    ? 'FUTURE'
    : 'CURRENT';
};

const toQualificationModel = (q: APIQualifications): Qualification => {
  const appointment_date = formatDateString(q.appointment_date, 'ui');
  const dismissal_date = formatDateString(q.dismissal_date, 'ui');

  const result: Qualification = {
    id: q.id,
    isBuiltIn: q.qualification_type === 0,
    qualificationId: q.qualification_id,
    qualificationName: q.qualification_name,
    assignmentType: q.work_type_id,
    assignmentTypeName: q.work_type_name,
    assignmentStatus: getAssignmentStatus(appointment_date, dismissal_date),
    representativeType: q.notification_officer_affiliation_type_id,
    representativeTypeName: q.notification_officer_affiliation_type_name,
    affiliateCompanyId: q.affiliate_company_id,
    affiliateCompanyName: q.affiliate_company_name,
    qualificationLevel: q.type,
    registeredUserId: q.registered_user_id,
    registeredName: q.registered_user_name,
    appointmentDate: appointment_date,
    dismissalDate: dismissal_date,
    comment: q.remarks,
    attachments:
      q.file_list == null
        ? []
        : q.file_list.map((a) => ({
            id: a.id,
            file_id: a.file_id,
            file_name: a.file_name,
            file_url: a.file_url,
            file_size: a.file_size,
            update_datetime: a.update_datetime,
            update_user_name: a.update_user_name,
            file_content: '',
          })),
  };
  return result;
};
